import {Button} from '@mantine/core';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import createChatForEntry from 'llm/data-capture/createChatForEntry';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface StartChatWithEntryButtonProps {
  entryId: string;
  isEditable: boolean;
}

enum EntryAssistantCta {
  AskQuestions = 'ask_questions',
  EditContents = 'edit_contents',
}

// There's only one render mode; kept as an enum (rather than a plain string) so the `renderMode`
// field on the CLICKED_ENTRY_ASSISTANT_CTA analytics event stays comparable to historical data.
enum EntryAssistantCtaRenderMode {
  SingleButton = 'single_button',
}

type AssistantScope = 'read' | 'write';

interface EntryAssistantCtaAnalyticsProperties {
  agentType: AgentType;
  assistantScope: AssistantScope;
  cta: EntryAssistantCta;
  entryId: string;
  isEditable: boolean;
  renderMode: EntryAssistantCtaRenderMode;
}

function getComposeLabel(isEditable: boolean): string {
  return isEditable ? 'Edit contents' : 'Ask';
}

function getComposeCta(isEditable: boolean): EntryAssistantCta {
  return isEditable ? EntryAssistantCta.EditContents : EntryAssistantCta.AskQuestions;
}

function getComposeAgentType(isEditable: boolean): AgentType {
  if (BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT) {
    return AgentType.ASSISTANT;
  }
  const canAsk = BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
  return isEditable || !canAsk ? AgentType.DATA_CAPTURE : AgentType.ASK;
}

export default function StartChatWithEntryButton({
  entryId,
  isEditable,
}: StartChatWithEntryButtonProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  // Before agent approvals, entry write access was sufficient to edit contents via chat. Once
  // approvals are released to the tenant, editing also requires approvals to be enabled for this
  // user, since they would otherwise have no way to request the access needed to approve edits.
  const canEditContents =
    isEditable &&
    (!BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED || BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS);
  const composeAgentType = getComposeAgentType(canEditContents);

  const getAnalyticsProperties = (
    cta: EntryAssistantCta,
    agentType: AgentType,
    assistantScope: AssistantScope
  ): EntryAssistantCtaAnalyticsProperties => ({
    agentType,
    assistantScope,
    cta,
    entryId,
    isEditable,
    renderMode: EntryAssistantCtaRenderMode.SingleButton,
  });

  const trackClickedCta = (properties: EntryAssistantCtaAnalyticsProperties, chatSessionId: string): void => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_ENTRY_ASSISTANT_CTA, {
      ...properties,
      chatSessionId,
    });
  };

  const startComposeChat = async (): Promise<void> => {
    const properties = getAnalyticsProperties(
      getComposeCta(canEditContents),
      composeAgentType,
      canEditContents ? 'write' : 'read'
    );
    setIsLoading(true);
    try {
      const {chatSessionId} = canEditContents
        ? await createChatForEntry(entryId, composeAgentType)
        : await createChatForEntry(entryId, composeAgentType, {assistantScope: 'read'});
      trackClickedCta(properties, chatSessionId);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      color="blue"
      leftIcon={<BenchlingIcon icon="sparkle" />}
      loading={isLoading}
      onClick={startComposeChat}
      size="xs"
      variant="secondary"
    >
      {getComposeLabel(canEditContents)}
    </Button>
  );
}
